/**
 * Integrytas Group — ErrorBanner (common).
 *
 * Human-readable error surface for data-fetch and action failures (P-11).
 * Raw technical strings (HTTP codes, gateway error identifiers) must never
 * be the primary thing a user sees — they belong behind the Details
 * disclosure, for support. Messages stay errors-only per §17.B7: this
 * banner REPLACES ad-hoc red toolbar codes, it never adds informational
 * noise.
 *
 * Usage:
 *   {error && (
 *     <ErrorBanner
 *       message={t('users.load_failed', "Couldn't load users.")}
 *       detail={error}                 // e.g. "CSM.API.HTTP_500"
 *       retry_label={t('common.retry', 'Retry')}
 *       on_retry={reload}
 *     />
 *   )}
 */

import { useState } from 'react';

export interface ErrorBannerProps {
  /** Already-localized, plain-language sentence: what failed. */
  message: string;
  /** Technical detail (error code, gateway message) — hidden behind Details. */
  detail?: string;
  /** Localized label for the retry button. Required when on_retry is passed. */
  retry_label?: string;
  on_retry?: () => void;
  /** Localized label for the details toggle. Defaults to "Details". */
  details_label?: string;
}

export function ErrorBanner({ message, detail, retry_label, on_retry, details_label }: ErrorBannerProps) {
  const [show_detail, set_show_detail] = useState(false);

  return (
    <div
      role="alert"
      className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-csm-danger-border bg-csm-danger-soft px-3 py-2 text-csm-sm text-csm-danger"
    >
      <span className="font-semibold">{message}</span>
      {on_retry && retry_label && (
        <button
          type="button"
          onClick={on_retry}
          className="rounded border border-csm-danger-border bg-white px-2 py-0.5 text-csm-xs font-semibold text-csm-danger hover:bg-csm-danger-soft"
        >
          {retry_label}
        </button>
      )}
      {detail && (
        <button
          type="button"
          onClick={() => set_show_detail(v => !v)}
          aria-expanded={show_detail}
          className="text-csm-xs underline decoration-dotted underline-offset-2 hover:opacity-80"
        >
          {details_label ?? 'Details'}
        </button>
      )}
      {detail && show_detail && (
        <code className="w-full break-all font-mono text-csm-xs opacity-80">{detail}</code>
      )}
    </div>
  );
}
